import axios from 'axios';
import type { AxiosResponse } from 'axios';

import {
  JZKRatesResponse,
  JZKRatesApiResponse,
  ApiErrorResponse,
  JZKValidityApiResponse,
  JZKValidityProcessedItem,
} from '../types';
import { processRates } from '../utils/currencyMapper';

const API_V1 = 'v1';
const API_JZK = 'jzk';

// Get API configuration from WordPress or environment variables
const getApiConfig = () => {
  // Check if we're in development mode and have environment variables
  const isDevelopment = import.meta.env.DEV;

  if (isDevelopment) {
    // Use environment variables for local development
    const devConfig = {
      baseURL: import.meta.env.VITE_API_URL || 'https://cdc-web-api.dev.betrain.net',
      token: import.meta.env.VITE_API_TOKEN || '',
    };

    return devConfig;
  }

  // Get configuration from WordPress localized data (production)
  const pluginData = window.cdcargoJzkPlugin;

  if (pluginData) {
    return {
      baseURL: pluginData.api_url || 'https://cdc-web-api.dev.betrain.net',
      token: pluginData.api_token || '',
    };
  }

  // Fallback configuration if WordPress data is not available
  return {
    baseURL: 'https://cdc-web-api.dev.betrain.net',
    token: '',
  };
};

const apiConfig = getApiConfig();

const api = axios.create({
  baseURL: apiConfig.baseURL,
  headers: {
    Authorization: `Bearer ${apiConfig.token}`,
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Helper function to check if response is an error response
const isErrorResponse = (data: unknown): data is ApiErrorResponse => {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const obj = data as Record<string, unknown>;
  return (
    'statusCode' in obj &&
    typeof obj.statusCode === 'number' &&
    'message' in obj &&
    typeof obj.message === 'string'
  );
};

export const jzkApi = {
  // Get current JŽK exchange rates
  getRates: async (): Promise<JZKRatesResponse> => {
    try {
      const url = `${API_V1}/${API_JZK}`;
      const response: AxiosResponse<JZKRatesApiResponse | ApiErrorResponse> = await api.get(url);

      // Check if the response is an error response
      if (isErrorResponse(response.data)) {
        throw new Error(response.data.message);
      }

      const apiResponse = response.data as JZKRatesApiResponse;

      // Process the rates
      const processedRates = processRates(apiResponse.items);

      // Return formatted response
      return {
        rates: processedRates,
      };
    } catch (error) {
      // Handle Axios errors
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.message) {
          throw new Error(error.response.data.message);
        }
        throw new Error(error.message);
      }

      // Handle other types of errors
      if (error instanceof Error) {
        throw error;
      }

      throw new Error('Nepodařilo se načíst kurzy JŽK.');
    }
  },

  // Get historical JŽK validity periods
  getValidity: async (): Promise<Record<number, JZKValidityProcessedItem[]>> => {
    try {
      const url = `${API_V1}/${API_JZK}/validity`;
      const response: AxiosResponse<JZKValidityApiResponse | ApiErrorResponse> = await api.get(url);

      // Check if the response is an error response
      if (isErrorResponse(response.data)) {
        throw new Error(response.data.message);
      }

      const apiResponse = response.data as JZKValidityApiResponse;

      // Process the response to create numbered items grouped by year
      const processedData: Record<number, JZKValidityProcessedItem[]> = {};

      apiResponse.items.forEach((yearItem) => {
        processedData[yearItem.year] = yearItem.validity.map((dateString, index) => ({
          number: index + 1, // Sequential numbering (first date = JŽK #1, last date = highest number)
          validFrom: dateString,
          year: yearItem.year,
        }));
      });

      return processedData;
    } catch (error) {
      // Handle Axios errors
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.message) {
          throw new Error(error.response.data.message);
        }
        throw new Error(error.message);
      }

      // Handle other types of errors
      if (error instanceof Error) {
        throw error;
      }

      throw new Error('Nepodařilo se načíst historická data JŽK.');
    }
  },
};
